import type { Middleware } from '@reduxjs/toolkit'
import { addBreadcrumb, captureMessage } from '@sentry/react'
import { LOGOUT } from './AuthConstants'

type LogoutContext = Record<string, unknown>

interface LogoutAction {
  type: typeof LOGOUT
  /** Set by explicit dispatch sites (manual logout, inactivity, etc.). */
  reason?: string
  /** Optional extra detail attached by the dispatching site. */
  logoutContext?: LogoutContext
  /** redux-state-sync marks actions it replays from another tab. */
  $isSync?: boolean
  /**
   * Present when LOGOUT is dispatched via restclient's refresh-failure path
   * (apiCall dispatches the refresh request's `error` type — which is LOGOUT —
   * with the failing request's error info as the payload).
   */
  payload?: {
    api?: string
    status?: number
    message?: string
    ErrorCode?: string
    UserFriendlyErrorCode?: number | string
  }
}

function isLogoutAction(action: unknown): action is LogoutAction {
  return typeof action === 'object' && action !== null && 'type' in action && action.type === LOGOUT
}

/**
 * Best-effort read of `waitingRoomReducer.inVisitRoom` without assuming the
 * loosely-typed legacy store shape. Answers "was the provider mid-visit?".
 */
function readInVisitRoom(state: unknown): boolean | undefined {
  if (typeof state !== 'object' || state === null || !('waitingRoomReducer' in state)) {
    return undefined
  }
  const wr = state.waitingRoomReducer
  if (typeof wr !== 'object' || wr === null || !('inVisitRoom' in wr)) {
    return undefined
  }
  return typeof wr.inVisitRoom === 'boolean' ? wr.inVisitRoom : undefined
}

function resolveReason(action: LogoutAction): { reason: string; context: LogoutContext } {
  // Explicit sites tag themselves — trust the tag.
  if (action.reason) {
    return { reason: action.reason, context: action.logoutContext ?? {} }
  }
  // The only LOGOUT dispatched *with an error payload but no reason* is restclient's
  // refresh-failure path. Surface the response detail so we can tell a network blip
  // (UserFriendlyErrorCode 512 / "Failed to fetch") from a real auth rejection.
  // Require a real error field — the generic `logout()` creator dispatches an empty
  // `payload: {}`, which must NOT be mislabeled as a refresh failure.
  if (action.payload && ('status' in action.payload || 'ErrorCode' in action.payload || 'api' in action.payload)) {
    return {
      reason: 'restclient-refresh-failed',
      context: {
        api: action.payload.api,
        status: action.payload.status,
        userFriendlyErrorCode: action.payload.UserFriendlyErrorCode,
        message: action.payload.message,
        errorCode: action.payload.ErrorCode,
      },
    }
  }
  return { reason: 'untagged-logout', context: {} }
}

/**
 * Observability-only middleware: records *why* each LOGOUT happened to Sentry.
 *
 * The reported mid-visit logouts leave no usable trace today — the token-refresh
 * failure paths dispatch LOGOUT with no Sentry reporting, so they're invisible in
 * Issues and only show up (if at all) as a generic "An unknown error occurred".
 * This tags every logout with a `logout_reason` and attaches enough context
 * (route, in-visit, connectivity, cross-tab origin) to tell the causes apart in
 * production.
 *
 * Purely additive — it reports, then passes the action through unchanged.
 */
export const logoutReportingMiddleware: Middleware = (store) => (next) => (action) => {
  if (isLogoutAction(action)) {
    const { reason, context } = resolveReason(action)
    const enriched: LogoutContext = {
      ...context,
      path: window.location.pathname,
      inVisit: readInVisitRoom(store.getState()),
      online: navigator.onLine,
      // Whether this logout was replayed from another tab by redux-state-sync
      // (a logout in a background tab logs out every tab — looks "random" to the user).
      crossTab: action.$isSync === true,
    }
    // Refresh-failure logouts are the ones we're hunting — make them stand out.
    const level: 'warning' | 'info' = reason.includes('refresh') ? 'warning' : 'info'
    // Always leave a breadcrumb (cheap, rides along on any real error event).
    addBreadcrumb({ category: 'auth', level, message: `logout: ${reason}`, data: enriched })
    // Capture every distinct logout as its own queryable Sentry event so any reason
    // (manual, inactivity, refresh failures, untagged) can be searched by logout_reason.
    // Skip only replayed cross-tab copies ($isSync): the originating tab already captured
    // it, so a single logout yields one event even with N tabs open. The `crossTab`
    // context field still records that propagation happened there.
    if (action.$isSync !== true) {
      captureMessage(`logout: ${reason}`, {
        level,
        tags: { logout_reason: reason },
        extra: enriched,
      })
    }
  }
  return next(action)
}
